import { useState, useMemo, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  Home, Building, Building2, KeyRound, Minus, Plus, Check, ArrowRight, ArrowLeft,
  Loader2, Sparkles, ShieldCheck, Leaf, CalendarDays, X,
} from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { estimatePrice, IMAGES, isRecurring } from "@/data/site";
import { submitLead } from "@/lib/api";
import { usePricing } from "@/context/PricingProvider";
import { DepositCheckout } from "@/components/DepositCheckout";
import { AddonsPicker, emptyAddons, addonsTotal, addonsSummary } from "@/components/AddonsPicker";
import { RecurringPromise, OfferBanner } from "@/components/RecurringPromise";import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { saveCart, clearSavedCart } from "@/lib/savedCart";

const DEPOSIT_THRESHOLD = 200;
const DEPOSIT_AMOUNT = 50;
const STEPS = ["Property", "Size", "Service", "Details", "Review"];

const PROPERTY = [
  { label: "House", icon: Home },
  { label: "Townhouse", icon: Building },
  { label: "Condo", icon: Building2 },
  { label: "Apartment", icon: KeyRound },
];
const SQFT = [
  { label: "Under 800 sq ft", value: 700 },
  { label: "800–1,500 sq ft", value: 1200 },
  { label: "1,500–2,500 sq ft", value: 2000 },
  { label: "2,500–3,500 sq ft", value: 3000 },
  { label: "3,500+ sq ft", value: 4000 },
];
const CLEAN_TYPES = [
  { key: "Standard Cleaning", label: "Standard", desc: "Routine upkeep" },
  { key: "Deep Cleaning", label: "Deep Clean", desc: "Thorough top-to-bottom" },
  { key: "Move-In / Move-Out", label: "Move-In / Out", desc: "Empty home readiness" },
];
const FREQS = [
  { key: "One-time", label: "One-time" },
  { key: "Weekly", label: "Weekly" },
  { key: "Biweekly", label: "Biweekly" },
  { key: "Monthly", label: "Monthly" },
];
const WEEKDAY_WINDOWS = [
  { key: "Morning (7am–11am)", label: "Morning", sub: "7am – 11am" },
  { key: "Midday (11am–3pm)", label: "Midday", sub: "11am – 3pm" },
  { key: "Afternoon (3pm–7pm)", label: "Afternoon", sub: "3pm – 7pm" },
  { key: "Flexible / any time", label: "Flexible", sub: "Any time" },
];
const SATURDAY_WINDOWS = [
  { key: "Morning (10am–1pm)", label: "Morning", sub: "10am – 1pm" },
  { key: "Afternoon (1pm–5pm)", label: "Afternoon", sub: "1pm – 5pm" },
  { key: "Flexible / any time", label: "Flexible", sub: "Any time" },
];
const windowsForDate = (d) => (d && d.getDay() === 6 ? SATURDAY_WINDOWS : WEEKDAY_WINDOWS);

function useIsDesktop() {
  const [d, setD] = useState(typeof window !== "undefined" ? window.innerWidth >= 768 : true);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const on = () => setD(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return d;
}

const Stepper = ({ label, value, set, min, max, testid }) => (
  <div className="flex items-center justify-between bg-white rounded-2xl border border-border px-5 py-4">
    <span className="font-medium text-navy">{label}</span>
    <div className="flex items-center gap-4">
      <button type="button" data-testid={`${testid}-minus`} onClick={() => set(Math.max(min, value - 1))}
        className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-navy hover:bg-navy hover:text-white transition-colors"><Minus className="w-4 h-4" /></button>
      <span className="w-6 text-center font-heading text-xl font-semibold text-navy" data-testid={`${testid}-value`}>{value}</span>
      <button type="button" data-testid={`${testid}-plus`} onClick={() => set(Math.min(max, value + 1))}
        className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-navy hover:bg-navy hover:text-white transition-colors"><Plus className="w-4 h-4" /></button>
    </div>
  </div>
);

const Chip = ({ active, onClick, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`px-4 py-2.5 rounded-full text-sm font-medium border transition-all duration-200 ${active ? "bg-leaf text-white border-leaf shadow-sm scale-[1.03]" : "bg-white text-navy border-border hover:border-leaf"}`}>
    {children}
  </button>
);

const Estimate = ({ est, className = "" }) => (
  <div className={className}>
    <p className="text-xs uppercase tracking-widest text-leaf flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Live estimate</p>
    <AnimatePresence mode="wait">
      <motion.p key={`${est.low}-${est.high}`} data-testid="bw-estimate-value"
        initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.2 }}
        className="font-heading text-3xl font-bold mt-1 text-navy">${est.low} – ${est.high}</motion.p>
    </AnimatePresence>
  </div>
);

const stepVariants = {
  enter: (dir) => ({ x: dir > 0 ? 24 : -24, opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir > 0 ? -24 : 24, opacity: 0 }),
};

const WizardBody = ({ isDesktop, onClose, prefill }) => {
  const pricing = usePricing();
  const p = prefill || {};
  const [step, setStep] = useState(prefill ? (prefill.step ?? 3) : 0);
  const [dir, setDir] = useState(1);
  const [propertyType, setPropertyType] = useState(p.propertyType || "House");
  const [bedrooms, setBedrooms] = useState(p.bedrooms ?? 2);
  const [bathrooms, setBathrooms] = useState(p.bathrooms ?? 2);
  const [sqft, setSqft] = useState(p.sqft ?? 1250);
  const [cleaningType, setCleaningType] = useState(p.cleaningType || "Standard Cleaning");
  const [frequency, setFrequency] = useState(p.frequency || "One-time");
  const [addonQty, setAddonQty] = useState(p.addonQty || emptyAddons());
  const [date, setDate] = useState(p.date || null);
  const [timeWindow, setTimeWindow] = useState(p.timeWindow || "Flexible / any time");
  const [contact, setContact] = useState(p.contact || { name: "", email: "", phone: "" });
  const [address, setAddress] = useState(p.address || "");
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [paid, setPaid] = useState(false);
  const [checkoutLeadId, setCheckoutLeadId] = useState(null);

  const est = useMemo(
    () => estimatePrice({ bedrooms, bathrooms, sqft, cleaningType, propertyType, frequency, addonsTotal: addonsTotal(addonQty, pricing.addons) }, pricing),
    [bedrooms, bathrooms, sqft, cleaningType, propertyType, frequency, addonQty, pricing]
  );
  const depositNeeded = est.low > DEPOSIT_THRESHOLD;
  const contactValid = contact.name.trim() && /\S+@\S+\.\S+/.test(contact.email) && address.trim().length > 4;

  // Persist the in-progress booking so returning visitors can resume ("saved cart")
  const engaged = step > 0 || !!contact.name.trim() || !!contact.email.trim() || !!address.trim() || !!addonsSummary(addonQty, pricing.addons);
  useEffect(() => {
    if (done || paid || !engaged) return;
    saveCart({
      step, propertyType, bedrooms, bathrooms, sqft, cleaningType, frequency, addonQty,
      date: date ? date.toISOString() : null, timeWindow, contact, address,
      estimate: { low: est.low, high: est.high }, savedAt: Date.now(),
    });
  }, [step, propertyType, bedrooms, bathrooms, sqft, cleaningType, frequency, addonQty, date, timeWindow, contact, address, est, engaged, done, paid]);
  useEffect(() => { if (done) clearSavedCart(); }, [done]);
  useEffect(() => {
    const keys = windowsForDate(date).map((w) => w.key);
    if (!keys.includes(timeWindow)) setTimeWindow("Flexible / any time");
  }, [date]);

  const go = (d) => { setDir(d); setStep((s) => Math.min(STEPS.length - 1, Math.max(0, s + d))); };

  const submit = async () => {
    if (!contactValid) { setDir(-1); setStep(3); toast.error("Please add your name, a valid email, and your service address."); return; }
    if (!agreed) { toast.error("Please agree to the Terms of Service to continue."); return; }
    setLoading(true);
    const dateStr = date ? format(date, "yyyy-MM-dd") : "";
    const selectedAddons = addonsSummary(addonQty, pricing.addons);
    const summary = `Booking — ${cleaningType}, ${propertyType}, ${bedrooms} bd / ${bathrooms} ba, ~${sqft} sq ft, ${frequency}${selectedAddons ? `. Add-ons: ${selectedAddons}` : ""}${dateStr ? `, preferred ${dateStr}` : ""}${timeWindow ? ` (${timeWindow})` : ""}. Estimated $${est.low}–$${est.high}.`;
    try {
      const lead = await submitLead({
        type: "booking", name: contact.name, email: contact.email, phone: contact.phone, address,
        service_category: "residential", service_type: cleaningType, property_type: propertyType,
        frequency, preferred_date: dateStr, preferred_time: timeWindow, addons: selectedAddons, estimate_low: est.low, estimate_high: est.high, message: summary,
        agreement_accepted: true, agreement_accepted_at: new Date().toISOString(),
      });
      if (lead.deposit_required) {
        setCheckoutLeadId(lead.id);
        setLoading(false);
        return;
      }
      setDone(true);
    } catch {
      toast.error("Something went wrong. Please try again or call us.");
      setLoading(false);
    }
  };

  if (checkoutLeadId && !done) {
    return (
      <div className="max-h-[90vh] overflow-y-auto overscroll-contain" data-testid="booking-wizard-checkout">
        <div className="px-6 pt-5 pb-1">
          <p className="text-xs uppercase tracking-[0.2em] text-leaf font-medium">Final step · Secure deposit</p>
          <h3 className="font-heading text-xl font-semibold text-navy tracking-tight mt-1">Pay your $50 deposit</h3>
        </div>
        <DepositCheckout
          leadId={checkoutLeadId}
          onPaid={() => { setPaid(true); setDone(true); }}
          onBack={() => setCheckoutLeadId(null)}
        />
      </div>
    );
  }

  if (done) {
    return (
      <div className="px-6 py-10 text-center" data-testid="booking-success">
        <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 220, damping: 16 }}
          className="w-16 h-16 rounded-full bg-leaf mx-auto flex items-center justify-center mb-5"><Check className="w-8 h-8 text-white" /></motion.div>
        <h3 className="font-heading text-2xl font-semibold text-navy mb-2">{paid ? "Deposit paid — you're booked!" : "Booking request received!"}</h3>
        <p className="text-muted-foreground mb-2">Estimated total for your {cleaningType.toLowerCase()}:</p>
        <p className="font-heading text-4xl font-bold text-navy mb-5">${est.low} – ${est.high}</p>
        <p className="text-sm text-muted-foreground mb-7 max-w-sm mx-auto">{paid
          ? "Your $50 deposit is received and applied to your total. We'll confirm your date and exact price shortly. Green Cleaning is included on every visit."
          : "We'll confirm your date and exact price shortly. Green Cleaning is included on every visit."}</p>
        <button onClick={onClose} data-testid="booking-done-btn" className="px-8 py-4 rounded-full bg-navy text-white font-medium hover:scale-105 transition-transform">Done</button>
      </div>
    );
  }

  const canNext = step === 3 ? contactValid : true;
  const isLast = step === STEPS.length - 1;

  const Content = (
    <AnimatePresence mode="wait" custom={dir}>
      <motion.div key={step} custom={dir} variants={stepVariants} initial="enter" animate="center" exit="exit"
        transition={{ duration: 0.25, ease: "easeOut" }} className="space-y-5" data-testid={`bw-step-${step}`}>
        {step === 0 && (
          <>
            <div><h3 className="font-heading text-2xl font-semibold text-navy tracking-tight">What are we cleaning?</h3><p className="text-muted-foreground text-sm mt-1">Pick the type of space.</p></div>
            <div className="grid grid-cols-2 gap-3">
              {PROPERTY.map(({ label, icon: Icon }) => {
                const active = propertyType === label;
                return (
                  <button key={label} type="button" onClick={() => setPropertyType(label)} data-testid={`bw-property-${label}`}
                    className={`group rounded-2xl border-2 p-5 text-left transition-all duration-200 hover:-translate-y-0.5 ${active ? "border-leaf bg-leaf/5 shadow-md" : "border-border bg-white hover:border-leaf/50"}`}>
                    <span className={`w-11 h-11 rounded-xl flex items-center justify-center mb-3 transition-colors ${active ? "bg-leaf text-white" : "bg-muted text-navy"}`}><Icon className="w-5 h-5" /></span>
                    <span className="block font-medium text-navy">{label}</span>
                  </button>
                );
              })}
            </div>
          </>
        )}

        {step === 1 && (
          <>
            <div><h3 className="font-heading text-2xl font-semibold text-navy tracking-tight">Tell us about the space</h3><p className="text-muted-foreground text-sm mt-1">Rough numbers are perfectly fine.</p></div>
            <div className="grid gap-3">
              <Stepper label="Bedrooms" value={bedrooms} set={setBedrooms} min={0} max={8} testid="bw-bedrooms" />
              <Stepper label="Bathrooms" value={bathrooms} set={setBathrooms} min={1} max={6} testid="bw-bathrooms" />
            </div>
            <div>
              <p className="text-navy/70 text-xs uppercase tracking-widest mb-2">Approx. square footage</p>
              <div className="flex flex-wrap gap-2">{SQFT.map((b) => <Chip key={b.value} active={sqft === b.value} onClick={() => setSqft(b.value)} testid={`bw-sqft-${b.value}`}>{b.label}</Chip>)}</div>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div><h3 className="font-heading text-2xl font-semibold text-navy tracking-tight">How deep, how often?</h3><p className="text-muted-foreground text-sm mt-1">Recurring plans save you more.</p></div>
            <div className="grid gap-3">
              {CLEAN_TYPES.map((t) => {
                const active = cleaningType === t.key;
                return (
                  <button key={t.key} type="button" onClick={() => setCleaningType(t.key)} data-testid={`bw-type-${t.key}`}
                    className={`flex items-center justify-between rounded-2xl border-2 p-4 text-left transition-all ${active ? "border-leaf bg-leaf/5" : "border-border bg-white hover:border-leaf/50"}`}>
                    <span><span className="block font-medium text-navy">{t.label}</span><span className="text-sm text-muted-foreground">{t.desc}</span></span>
                    <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${active ? "border-leaf bg-leaf" : "border-border"}`}>{active && <Check className="w-3 h-3 text-white" />}</span>
                  </button>
                );
              })}
            </div>
            <div>
              <p className="text-navy/70 text-xs uppercase tracking-widest mb-2">Frequency</p>
              <div className="flex flex-wrap gap-2">
                {FREQS.map((f) => <Chip key={f.key} active={frequency === f.key} onClick={() => setFrequency(f.key)} testid={`bw-freq-${f.key}`}>{f.label}{f.save && <span className="ml-1 text-[10px] opacity-80">{f.save}</span>}</Chip>)}
              </div>
            </div>
            {isRecurring(frequency) && (
              <div className="space-y-3">
                <RecurringPromise variant="card" promise={pricing.recurring_promise} />
              </div>
            )}
            <div>
              <p className="text-navy/70 text-xs uppercase tracking-widest mb-2">Add-ons (optional)</p>
              <AddonsPicker qty={addonQty} setQty={setAddonQty} addons={pricing.addons} />
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <div><h3 className="font-heading text-2xl font-semibold text-navy tracking-tight">When & how to reach you</h3><p className="text-muted-foreground text-sm mt-1">Pick a preferred date — we'll confirm availability.</p></div>
            <div className="grid lg:grid-cols-2 gap-5 items-start">
              <div className="rounded-2xl border border-border bg-white p-2 flex justify-center">
                <Calendar mode="single" selected={date} onSelect={setDate} disabled={[{ before: new Date() }, { dayOfWeek: [0] }]} data-testid="bw-date" />
                {/* Sundays are disabled — closed */}
              </div>
              <div className="space-y-3">
                <Input data-testid="bw-name" className="h-12 bg-white" placeholder="First name" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} />
                <Input data-testid="bw-email" type="email" className="h-12 bg-white" placeholder="Email address" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} />
                <Input data-testid="bw-phone" className="h-12 bg-white" placeholder="Phone number" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} />
                <AddressAutocomplete value={address} onChange={setAddress} />
                <p className="text-xs text-muted-foreground">Start typing your address and pick it from the list — or type it in full.</p>
                {date && <p className="text-sm text-leaf flex items-center gap-1.5"><CalendarDays className="w-4 h-4" /> {format(date, "EEEE, MMM d, yyyy")}</p>}
              </div>
            </div>
            <div>
              <p className="text-navy/70 text-xs uppercase tracking-widest mb-2">Preferred time window</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="bw-time-windows">
                {windowsForDate(date).map((t) => {
                  const active = timeWindow === t.key;
                  return (
                    <button key={t.key} type="button" onClick={() => setTimeWindow(t.key)} data-testid={`bw-time-${t.label}`}
                      className={`rounded-2xl border-2 py-3 px-2 text-center transition-all ${active ? "border-leaf bg-leaf/10" : "border-border bg-white hover:border-leaf/50"}`}>
                      <span className="block font-medium text-navy text-sm">{t.label}</span>
                      <span className="block text-xs text-muted-foreground mt-0.5">{t.sub}</span>
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground mt-2">We'll do our best to hit your window and confirm the exact arrival time. Hours: Mon–Fri 7am–7pm, Sat 10am–5pm, closed Sun.</p>
            </div>
          </>
        )}

        {step === 4 && (
          <>
            <div><h3 className="font-heading text-2xl font-semibold text-navy tracking-tight">Almost done — review</h3><p className="text-muted-foreground text-sm mt-1">Here's your cleaning at a glance.</p></div>
            <div className="rounded-2xl border border-border bg-white divide-y divide-border" data-testid="bw-summary">
              {[
                ["Property", propertyType],
                ["Size", `${bedrooms} bd · ${bathrooms} ba · ~${sqft.toLocaleString()} sq ft`],
                ["Service", `${CLEAN_TYPES.find((c) => c.key === cleaningType)?.label} · ${frequency}`],
                ["Add-ons", addonsSummary(addonQty, pricing.addons) || "None"],
                ["Preferred date", date ? format(date, "MMM d, yyyy") : "Flexible"],
                ["Preferred time", timeWindow],
                ["Address", address || "—"],
                ["Contact", `${contact.name || "—"}${contact.email ? ` · ${contact.email}` : ""}`],
              ].map(([k, v]) => (
                <div key={k} className="flex items-center justify-between px-4 py-3 text-sm"><span className="text-muted-foreground">{k}</span><span className="font-medium text-navy text-right">{v}</span></div>
              ))}
              <div className="flex items-center justify-between px-4 py-4 bg-navy text-white rounded-b-2xl"><span className="text-sm text-white/70">Estimated total</span><span className="font-heading text-2xl font-bold">${est.low} – ${est.high}</span></div>
            </div>
            {depositNeeded && (
              <div className="flex items-start gap-3 rounded-2xl bg-leaf/10 border border-leaf/30 p-4" data-testid="bw-deposit-note">
                <ShieldCheck className="w-5 h-5 text-leaf shrink-0 mt-0.5" />
                <p className="text-sm text-navy">A refundable <strong>${DEPOSIT_AMOUNT} deposit</strong> secures your spot and is applied to your total. You'll finish on Stripe's secure checkout.</p>
              </div>
            )}
            <div className="flex items-center gap-2 text-sm text-muted-foreground"><Leaf className="w-4 h-4 text-leaf" /> Green Cleaning included · fully insured & background-checked pros</div>
            <label className="flex items-start gap-3 rounded-2xl border border-border bg-white p-4 cursor-pointer" data-testid="bw-terms-label">
              <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} data-testid="bw-terms-checkbox" className="mt-0.5 w-5 h-5 accent-leaf shrink-0" />
              <span className="text-sm text-navy">I agree to the <a href="/service-agreement" target="_blank" rel="noreferrer" className="text-leaf font-medium underline underline-offset-2">CJB Clean Service Agreement</a> and cancellation policy.</span>
            </label>
          </>
        )}
      </motion.div>
    </AnimatePresence>
  );

  const Footer = (
    <div className="flex items-center gap-3">
      {step > 0 && (
        <button type="button" onClick={() => go(-1)} data-testid="bw-back"
          className="px-5 py-3.5 rounded-full border border-border text-navy font-medium hover:bg-muted transition-colors flex items-center gap-2"><ArrowLeft className="w-4 h-4" /> Back</button>
      )}
      {!isLast ? (
        <button type="button" onClick={() => go(1)} disabled={!canNext} data-testid="bw-next"
          className="flex-1 py-3.5 rounded-full bg-leaf text-white font-medium flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform disabled:opacity-50 disabled:hover:scale-100">
          Continue <ArrowRight className="w-4 h-4" />
        </button>
      ) : (
        <button type="button" onClick={submit} disabled={loading || !agreed} data-testid="bw-submit"
          className="flex-1 py-3.5 rounded-full bg-leaf text-white font-medium flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform disabled:opacity-60">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (depositNeeded ? `Book & Pay $${DEPOSIT_AMOUNT} Deposit` : "Confirm Booking")}
        </button>
      )}
    </div>
  );

  const ProgressBar = (
    <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
      <motion.div className="h-full bg-leaf" initial={false} animate={{ width: `${((step + 1) / STEPS.length) * 100}%` }} transition={{ duration: 0.3 }} />
    </div>
  );

  const Header = (
    <div className="flex items-center justify-between mb-1">
      <p className="text-xs uppercase tracking-[0.2em] text-leaf font-medium">Step {step + 1} of {STEPS.length} · {STEPS[step]}</p>
    </div>
  );

  if (isDesktop) {
    return (
      <div className="grid grid-cols-[1.35fr_1fr] max-h-[92vh]" data-testid="booking-wizard">
        <div className="p-8 flex flex-col min-h-0">
          {Header}
          <div className="mb-6">{ProgressBar}</div>
          <div className="flex-1 min-h-0 overflow-y-auto pr-1">{Content}</div>
          <div className="mt-7">{Footer}</div>
        </div>
        <div className="relative bg-navy text-white p-8 flex flex-col justify-between overflow-hidden min-h-0">
          <img src={IMAGES.residential} alt="" className="absolute inset-0 w-full h-full object-cover opacity-15" />
          <div className="relative z-10">
            <div className="inline-flex items-center gap-2 text-leaf mb-6"><Leaf className="w-5 h-5" /><span className="text-sm font-medium">Green Cleaning included</span></div>
            <Estimate est={est} className="text-white [&_p]:text-white [&_.text-navy]:text-white" />
            <p className="text-white/60 text-sm mt-3 max-w-[240px]">Final price is confirmed after a quick review — no surprises.</p>
          </div>
          <div className="relative z-10 space-y-2 text-sm text-white/70">
            <p className="flex items-center gap-2"><Check className="w-4 h-4 text-leaf" /> Background-checked professionals</p>
            <p className="flex items-center gap-2"><Check className="w-4 h-4 text-leaf" /> Satisfaction guaranteed</p>
            <p className="flex items-center gap-2"><Check className="w-4 h-4 text-leaf" /> Eco-friendly products, every visit</p>
          </div>
        </div>
      </div>
    );
  }

  // Mobile
  return (
    <div className="flex flex-col h-[90vh]" data-testid="booking-wizard">
      <div className="px-5 pt-5 pb-3 shrink-0">
        {Header}
        <div className="mt-2">{ProgressBar}</div>
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto px-5 pb-4">{Content}</div>
      <div className="border-t border-border bg-white px-5 py-4 shrink-0">
        <div className="flex items-center justify-between mb-3">
          <Estimate est={est} />
          {depositNeeded && <span className="text-xs px-3 py-1.5 rounded-full bg-leaf/10 text-leaf font-medium">$50 deposit</span>}
        </div>
        {Footer}
      </div>
    </div>
  );
};

export const BookingWizard = ({ open, onOpenChange, prefill }) => {
  const isDesktop = useIsDesktop();
  const close = useCallback(() => onOpenChange(false), [onOpenChange]);

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden gap-0 border-border max-h-[92vh]" data-lenis-prevent data-testid="booking-modal">
          <DialogTitle className="sr-only">Book Your Cleaning Online</DialogTitle>
          <DialogDescription className="sr-only">Answer a few quick questions to get an instant price and book your cleaning.</DialogDescription>
          {open && <WizardBody isDesktop onClose={close} prefill={prefill} />}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange} dismissible={false}>
      <DrawerContent className="max-h-[94vh]" data-lenis-prevent data-testid="booking-modal">
        <DrawerTitle className="sr-only">Book Your Cleaning Online</DrawerTitle>
        <button onClick={close} aria-label="Close" data-testid="booking-close-btn"
          className="absolute right-4 top-3 z-50 w-9 h-9 rounded-full bg-muted/90 flex items-center justify-center text-navy hover:bg-muted transition-colors">
          <X className="w-5 h-5" />
        </button>
        {open && <WizardBody isDesktop={false} onClose={close} prefill={prefill} />}
      </DrawerContent>
    </Drawer>
  );
};
